'use client';

import { useMemo } from 'react';
import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchCasesList,
  provenanceKeys,
  type CaseSummary,
} from '@/shared/cors-provenance-api';
import { fetchPermissionedCasesList } from '@/shared/cases-permission-client';
import { applyAdHocCaseListPolicy } from '@/shared/ad-hoc-evidence';
import { useReactiveAuthTokens, useReactiveFeatureFlagValue } from '@/app/contexts';
import { getTenantIdFromToken } from '@/shared/csm-utils';
import { useProvenanceSession } from './useProvenanceSession';
import { usePersonalFilesCaseId } from './usePersonalFilesCaseId';
import { isWafBlockError } from '@/shared/waf-block';

/** Session + rollout inputs that decide which cases source is queried. */
export type CasesListQueryInputs = {
  cellId: string | null;
  accessToken: string | null;
  /** LaunchDarkly ``casesFromPermissionService``. */
  fromPermissionService: boolean;
  /** Session tenant, which a CSM can switch without the cellId changing. */
  tenantId: string | null;
};

/**
 * Key + fetcher for the cases list, shared so callers that cannot use
 * {@link useCasesList} still hit the *same* cache entry.
 *
 * ``Main`` is one such caller: it renders ``AuthContext.Provider`` itself, so
 * the context-reading hooks return their defaults inside it (the same trap
 * already noted for ``FeaturesContext`` in ``app/main.tsx``). Calling
 * ``useCasesList`` there yielded a permanently disabled query keyed on a null
 * cellId — a second, always-empty cache entry — which is why threat-report
 * creation could not resolve a caseTag while the nav dropdown listed the case.
 */
export function casesListQueryOptions(inputs: CasesListQueryInputs) {
  const { cellId, accessToken, fromPermissionService, tenantId } = inputs;
  return queryOptions<CaseSummary[], Error>({
    queryKey: fromPermissionService
      ? [...provenanceKeys.cases(), 'permissioned', cellId, tenantId]
      : provenanceKeys.casesList(cellId),
    queryFn: ({ signal }) =>
      fromPermissionService
        ? fetchPermissionedCasesList({ signal })
        : fetchCasesList({
            cellId: cellId as string,
            accessToken: accessToken as string,
            signal,
          }),
    staleTime: 5 * 60 * 1000,
    // A WAF block lasts minutes and counts every retry against the same rate
    // limit, so retrying only extends it (TR-3920). Other errors keep the
    // react-query default of 3.
    retry: (failureCount, error) => !isWafBlockError(error) && failureCount < 3,
  });
}

/**
 * TanStack Query hook for the header "All Cases" dropdown.
 *
 * Source is feature-flagged for a slow rollout (LaunchDarkly
 * ``casesFromPermissionService``):
 *   - flag ON  → permission-service ``/user/cases`` (access-aware: tenant
 *     master switch + per-case grants + accessible_to_all).
 *   - flag OFF → legacy provenance ``/cases`` (unfiltered; current default).
 *
 * Both branches return the same {@link CaseSummary} shape, so the downstream
 * consumers are unaffected by the flip. Auth wiring (``cellId`` +
 * ``realAccessToken`` + ``enabled``) lives in ``useProvenanceSession``; the
 * query is disabled until the session is ready.
 */
export function useCasesList(): UseQueryResult<CaseSummary[], Error> & { cellId: string | null } {
  const { cellId, accessToken, enabled } = useProvenanceSession();
  const fromPermissionService =
    useReactiveFeatureFlagValue('casesFromPermissionService') === true;
  const adHocEnabled = useReactiveFeatureFlagValue('adHoc') === true;
  const personalFilesCaseId = usePersonalFilesCaseId();

  const selectCases = useMemo(
    () => (cases: CaseSummary[]) =>
      applyAdHocCaseListPolicy(cases, adHocEnabled, personalFilesCaseId),
    [adHocEnabled, personalFilesCaseId],
  );

  // The permission-service branch resolves cases for the session's current
  // tenant, which a CSM can switch without the cellId changing. Scope the key
  // by tenant id so a tenant switch doesn't serve the previous tenant's cached
  // list for the query's staleTime.
  const { idToken } = useReactiveAuthTokens();
  const tenantId = useMemo(() => getTenantIdFromToken(idToken), [idToken]);

  const query = useQuery<CaseSummary[], Error, CaseSummary[]>({
    ...casesListQueryOptions({ cellId, accessToken, fromPermissionService, tenantId }),
    // The permission-service branch hits /api/protected/cases/access, which
    // requires a tenant (resolveUserContext); stay disabled until tenant
    // context resolves so a CSM doesn't get a failing request / cached error
    // before a tenant switch completes. The legacy provenance branch has no
    // tenant precondition, so its gating stays the session-only `enabled`.
    enabled: enabled && (!fromPermissionService || Boolean(tenantId)),
    select: selectCases,
  });

  return Object.assign(query, { cellId });
}
